import * as csstree from 'css-tree';
import { validateCustomDesign } from './validateCustomDesign.js';

const namespace = globalThis.crypto?.randomUUID?.().replace(/-/g, '') || Math.random().toString(36).slice(2);
let nextId = 0;
export function newAnimationName() { return `ufo_custom_${namespace}_${++nextId}`; }

// Cards style the card surface and its text; other sections with image modules style their images.
export function customDesignKind(section) { return section?.type === 'Cards' ? 'cards' : 'images'; }

export function resolveCardDesign(section) {
    if (!section?.customDesign) return {style: {}, animation: null, targets: {}};
    try {
        const animation = {};
        const targets = {};
        const style = validateCustomDesign(csstree, section.customDesign, animation, targets);
        return {style, animation: animation.keyframes ? animation : null, targets};
    } catch (_) { return {style: {}, animation: null, targets: {}}; }
}

// Card hover targets react to hovering anywhere on the card; links react to the link itself.
const cardBase = scope => `.ufo-sec--cards .ufo--card.${scope}`;
const TARGET_SELECTORS = {
    cards: scope => `${cardBase(scope)} .text-container`,
    cardsHover: scope => `${cardBase(scope)}:hover .text-container`,
    headings: scope => `${cardBase(scope)} .text-container :is(h1,h2,h3,h4,h5,h6)`,
    headingsHover: scope => `${cardBase(scope)}:hover .text-container :is(h1,h2,h3,h4,h5,h6)`,
    text: scope => `${cardBase(scope)} .text-container :is(p,li)`,
    textHover: scope => `${cardBase(scope)}:hover .text-container :is(p,li)`,
    links: scope => `${cardBase(scope)} .text-container a`,
    linksHover: scope => `${cardBase(scope)} .text-container a:hover`
};
// The published Tabs section wraps its image in .tabs-module instead of .media-container.
// Mockups and thumbnail layouts render their own screenshots, which are not the section's image;
// stages without a device frame are marked .no-mockup and still hold the section's image.
const image = (scope, state = '') => `.ufo-sec.${scope} :is(.media-container, .tabs-module:not(.text))${state} img:not(.thumbnail-layout-preview *, [class*="mockup"]:not(.no-mockup) *)`;
const IMAGE_SELECTORS = {
    images: scope => image(scope),
    imagesHover: scope => image(scope, ':hover'),
    captions: scope => `.ufo-sec.${scope} .media-caption`
};
const KINDS = {
    cards: {base: 'cards', selectors: TARGET_SELECTORS},
    images: {base: 'images', selectors: IMAGE_SELECTORS}
};
const HOVER_BASES = {cardsHover: 'cards', headingsHover: 'headings', textHover: 'text', linksHover: 'links', imagesHover: 'images'};
const block = style => Object.entries(style).map(([property, value]) => `${property}:${value};`).join('');

const withAnimation = (style, animation, animationName) => !animation || !animationName ? style : {
    ...style, 'animation-name': animationName, 'animation-duration': animation.duration,
    'animation-timing-function': animation.easing,
    'animation-direction': animation.direction, 'animation-iteration-count': 'infinite'
};

export function cardCustomStyle(section, animationName) {
    if (section?.type !== 'Cards') return {};
    const {style, animation} = resolveCardDesign(section);
    return withAnimation(style, animation, animationName);
}

// Cards style the card's visible surface (.text-container), not the click-to-select wrapper around it;
// other sections style their images. Targets of the other kind are ignored.
// Generated CSS can't declare transitions, so hover states get a short one on the element they change.
export function cardCustomCss(section, scope) {
    if (!/^ufo_custom_[a-z0-9]+_[0-9]+$/.test(scope)) return '';
    const {style, animation, targets} = resolveCardDesign(section);
    const {base, selectors} = KINDS[customDesignKind(section)];
    const styles = Object.fromEntries(Object.entries({cards: style, ...targets}).filter(([target]) => selectors[target]));
    styles[base] = withAnimation(styles[base] || {}, animation, scope);
    const rules = Object.entries(styles).filter(([, declarations]) => Object.keys(declarations).length)
        .map(([target, declarations]) => `${selectors[target](scope)}{${block(declarations)}}`).join('');
    const transitions = Object.entries(HOVER_BASES).filter(([hover]) => styles[hover] && Object.keys(styles[hover]).length)
        .map(([hover, hoverBase]) => `${selectors[hoverBase](scope)}{transition-property:${Object.keys(styles[hover]).join(',')};transition-duration:0.2s;transition-timing-function:ease;}`)
        .join('');
    return rules + (transitions ? `@media (prefers-reduced-motion: no-preference){${transitions}}` : '') + cardAnimationCss(section, scope);
}

export function cardAnimationCss(section, animationName) {
    if (!/^ufo_custom_[a-z0-9]+_[0-9]+$/.test(animationName)) return '';
    const {animation} = resolveCardDesign(section);
    if (!animation) return '';
    const frames = animation.keyframes.map(frame => `${frame.offset}%{${block(frame.declarations)}}`).join('');
    return `@media (prefers-reduced-motion: no-preference){@keyframes ${animationName}{${frames}}}`;
}
